import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { diagnosisLabel } from "../constants/diseaseOptions";
import {
  FiFileText,
  FiSearch,
  FiInfo,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiLock,
  FiLoader,
} from "react-icons/fi";
import { storage } from "../firebase";
// No firebase/firestore import here any more: this page reaches Firestore only
// through esignLookup / esignSubmit. Storage is still direct — the PDF upload
// is audit B2 and is fixed with the rest of storage.rules.
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { REFERRAL_DEFAULT_SITE } from "../services/firestorePaths";
import { jsPDF } from "jspdf";

// Patient-facing e-sign consent flow (/esign/:siteId/:token, /esign/:token).
//
// Opened by patients from an SMS link, almost always on a phone, so this is a
// step-by-step wizard anyone can finish without help. Styled to match the RegX
// login aesthetic exactly: layered dark background + glow orbs, frosted glass
// cards, glassy inputs, and the purple→indigo→blue gradient buttons.
//
// Data paths resolve against the site in the LINK, not the active site (D-16).
// This page runs on a patient's phone, which has never had an active site set,
// so the active-site helpers always fell back to the default (lin-dubois1) and
// a link minted at any other site was never found. All three hops below — find
// the patient, write the consent, stamp the record — must use the SAME site, or
// a consent gets filed under a site the patient does not belong to.
//
// The signed PDF is two pages: consent + signature on page 1, and a full audit
// trail (link creation, SMS send, identity check, open, review, signature,
// signer IP + device) on page 2.

const LOGO_SRC = "/xiva_research.png";
const POWERED_BY = "Powered by Lung Innovations Network";

// PDF palette (clean, light, professional — the doc prints on white paper)
const INDIGO = [79, 70, 229];
const VIOLET = [124, 58, 237];
const INK = [30, 41, 59]; // slate-800
const SLATE = [71, 85, 105]; // slate-600
const MUTED = [148, 163, 184]; // slate-400
const HAIRLINE = [226, 232, 240]; // slate-200

// ---- Module-scope presentational pieces (stable identity across renders) --
const Shell = ({ children }) => (
  <div className="relative min-h-screen w-full text-slate-100 font-[Manrope] flex flex-col">
    {/* Layered RegX background */}
    <div className="fixed inset-0 -z-10 overflow-hidden">
      <img
        src="/bg_ai.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.28),_transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(37,99,235,0.22),_transparent_55%)]" />
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
      <div className="absolute -bottom-32 left-1/3 w-[480px] h-[480px] rounded-full bg-fuchsia-600/15 blur-[140px]" />
    </div>

    <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center mb-6">
          <div className="rounded-xl bg-white/95 px-4 py-2 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.6)] ring-1 ring-white/20">
            <img src={LOGO_SRC} alt="Xiva Research" className="h-7 object-contain" />
          </div>
          <span className="mt-2.5 text-[10px] uppercase tracking-[0.25em] text-slate-400">
            {POWERED_BY}
          </span>
        </div>
        {children}
      </div>
    </div>

    <footer className="relative pb-5 px-4 text-center">
      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center justify-center gap-1.5">
        <FiLock size={11} /> Your information is private &amp; secure
      </p>
    </footer>
  </div>
);

const Card = ({ children }) => (
  <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_20px_60px_-20px_rgba(124,58,237,0.5)] overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10 pointer-events-none" />
    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70" />
    <div className="relative p-5 sm:p-6">{children}</div>
  </div>
);

const SectionHeader = ({ children }) => (
  <div className="flex items-center gap-2 mb-3">
    <span className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold">
      {children}
    </span>
    <span className="h-px flex-1 bg-white/10" />
  </div>
);

const INPUT_CLASS =
  "w-full px-3.5 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition";

const SELECT_CLASS =
  "w-full px-2.5 py-3 rounded-xl bg-white/[0.06] border border-white/10 text-slate-100 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition";

const GhostButton = ({ children, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="px-4 py-3 rounded-xl border border-white/10 bg-white/[0.04] text-slate-300 font-medium hover:bg-white/[0.08] disabled:opacity-50 transition flex items-center gap-2"
  >
    {children}
  </button>
);

const PrimaryButton = ({ children, onClick, disabled, type = "button" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className="group relative w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white font-semibold ring-1 ring-white/20 hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
  >
    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
    <span className="relative flex items-center justify-center gap-2">{children}</span>
  </button>
);

export default function ESignPage() {
  // The consent link carries the site: /esign/:siteId/:token. Legacy links
  // (/esign/:token) have no siteId — every one of those was minted for the
  // Dubois site, which is exactly REFERRAL_DEFAULT_SITE. Same fallback the
  // referral questionnaire uses, for the same reason.
  const { token, siteId } = useParams();
  const site = siteId || REFERRAL_DEFAULT_SITE;

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wizard: 1 = verify identity, 2 = review & agree, 3 = sign & submit
  const [step, setStep] = useState(1);

  const [dobMonth, setDobMonth] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [dobError, setDobError] = useState("");

  const [showFullText, setShowFullText] = useState(false);
  const [printName, setPrintName] = useState("");
  const [approved, setApproved] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveStage, setSaveStage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);

  // Signature canvas
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Audit-trail capture
  const logoRef = useRef(null);
  const openedAtRef = useRef(new Date());
  const dobVerifiedAtRef = useRef(null);
  const approvedAtRef = useRef(null);
  const [clientIP, setClientIP] = useState("Unavailable");
  const [userAgent, setUserAgent] = useState("");

  const consentText = useMemo(
    () => `Principal Investigator: Sandeep Bansal, MD
Research Site: Clinical Research Associates of Central PA, 190 West Park Avenue, Suite 8, Dubois, PA 15801

You have been referred by your provider to clinical research. If you sign this authorization form, you agree to the use and disclosure (release) of your health information to the research team, as described in this Authorization Form. The following health information about you may be collected such as: Past medical history, medication history/lists, diagnoses, laboratory results including pathology, imaging results, cardiopulmonary results, operative reports, consultative reports, physician progress notes, and hospitalization reports.

Your health information may be used for screening purposes by the research site, the principal investigator, and their representatives. If you qualify for a research study this authorization gives Clinical Research Associates of Central PA permission to contact you regarding research studies available at our site.

You are required to sign this form to be prescreened for a research study. If you do not sign this form, it will not affect your treatment, or enrollment or eligibility for benefits. You may change your mind and revoke (take back) this authorization form at any time unless your health information is already disclosed based on this authorization form.

To revoke this Authorization, you must write to: Lung Innovations Network, P.C., 190 West Park Avenue, Suite 8, Dubois, PA 15801. This Authorization will not expire unless you revoke it.

* If this is signed by an authorized personal representative of the participant, indicate the representative's relationship to the participant or authority to act on behalf of the participant.`,
    []
  );

  // ---- Load patient by token --------------------------------------------
  // Via a Cloud Function rather than a Firestore query. The lookup is by an
  // unguessable token, and Firestore rules cannot scope a query to "only the
  // document whose token you hold" — so allowing this read from the browser
  // meant allowing the whole collection to be LISTED. The function checks the
  // token against one document and returns only that patient.
  useEffect(() => {
    const fetchPatient = async () => {
      try {
        const resp = await fetch(`${FUNCTIONS_BASE}/esignLookup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ siteId: site, token }),
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data?.patient) setPatient(data.patient);
        }
        // A 404 is the "this link isn't valid" case and leaves patient null,
        // which renders exactly what a missing record used to.
      } catch (err) {
        console.error("Failed to fetch patient:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPatient();
  }, [token, site]);

  // ---- Background audit capture (never blocks the flow) ------------------
  useEffect(() => {
    setUserAgent(navigator.userAgent || "Unknown device");
    fetch("https://api.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => data?.ip && setClientIP(data.ip))
      .catch(() => {});
  }, []);

  // ---- Preload the logo once so PDF generation doesn't wait on it --------
  useEffect(() => {
    const img = new Image();
    img.src = LOGO_SRC;
    img.onload = () => {
      logoRef.current = img;
    };
  }, []);

  // ---- Signature canvas: retina-correct + resize-aware -------------------
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ratio = window.devicePixelRatio || 1;
    const cssWidth = container.clientWidth;
    const cssHeight = 190;

    canvas.width = Math.round(cssWidth * ratio);
    canvas.height = Math.round(cssHeight * ratio);
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0); // draw in CSS pixels, render sharp
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, cssWidth, cssHeight);

    ctx.strokeStyle = "#c7d2fe"; // indigo-200 baseline
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(18, cssHeight - 32);
    ctx.lineTo(cssWidth - 18, cssHeight - 32);
    ctx.stroke();

    ctxRef.current = ctx;
    setHasInk(false);
  }, []);

  useEffect(() => {
    if (step !== 3) return;
    initCanvas();
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(initCanvas);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [step, initCanvas]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches && e.touches[0];
    return {
      x: (touch ? touch.clientX : e.clientX) - rect.left,
      y: (touch ? touch.clientY : e.clientY) - rect.top,
    };
  };

  const handleStart = (e) => {
    e.preventDefault();
    const ctx = ctxRef.current;
    if (!ctx) return;
    drawingRef.current = true;
    const { x, y } = getPos(e);
    ctx.strokeStyle = "#312e81"; // indigo-900 ink
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const handleMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = ctxRef.current;
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const handleEnd = () => {
    drawingRef.current = false;
  };

  const handleClear = () => initCanvas();

  // Downscale the (retina) signature to a fixed, small bitmap. Keeps the
  // embedded PNG tiny regardless of device pixel ratio — the main thing that
  // was making submit slow on phones.
  const getSignatureDataUrl = () => {
    const src = canvasRef.current;
    const out = document.createElement("canvas");
    out.width = 500;
    out.height = 125;
    const octx = out.getContext("2d");
    octx.fillStyle = "#ffffff";
    octx.fillRect(0, 0, out.width, out.height);
    octx.drawImage(src, 0, 0, out.width, out.height);
    return out.toDataURL("image/png");
  };

  // ---- Step 1: identity --------------------------------------------------
  const normalizeDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const handleVerifyDob = () => {
    if (!dobMonth || !dobDay || !dobYear) {
      setDobError("Please enter your full date of birth.");
      return;
    }
    const combined = `${dobYear}-${dobMonth}-${dobDay}`;
    if (normalizeDate(combined) === normalizeDate(patient?.dob)) {
      dobVerifiedAtRef.current = new Date();
      setDobError("");
      setStep(2);
    } else {
      setDobError("That date of birth doesn't match our records. Please try again.");
    }
  };

  // ---- Step 2: review ----------------------------------------------------
  const handleApproveToggle = (checked) => {
    setApproved(checked);
    if (checked && !approvedAtRef.current) approvedAtRef.current = new Date();
    if (checked) setReviewError("");
  };

  const handleContinueToSign = () => {
    if (!printName.trim()) {
      setReviewError("Please type your full name.");
      return;
    }
    if (!approved) {
      setReviewError("Please check the box to agree before continuing.");
      return;
    }
    setReviewError("");
    setStep(3);
  };

  // ---- PDF generation ----------------------------------------------------
  const fmtDate = (val) => {
    if (!val) return "—";
    if (val.toDate) return val.toDate().toLocaleString();
    if (val instanceof Date) return val.toLocaleString();
    if (typeof val === "string") return val;
    return new Date(val).toLocaleString();
  };

  const buildPdf = (submittedAt) => {
    const docPdf = new jsPDF({ unit: "pt", format: "letter", compress: true });
    const margin = 52;
    const pageWidth = docPdf.internal.pageSize.getWidth();
    const pageHeight = docPdf.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;
    const centerX = pageWidth / 2;

    const eyebrow = (text, x, yy) => {
      docPdf.setFont("helvetica", "bold").setFontSize(7.5).setTextColor(...INDIGO);
      docPdf.text(text.toUpperCase(), x, yy, { charSpace: 0.6 });
    };
    const hairline = (yy) => {
      docPdf.setDrawColor(...HAIRLINE);
      docPdf.setLineWidth(0.8);
      docPdf.line(margin, yy, pageWidth - margin, yy);
    };

    // ===== PAGE 1: consent + signature (single page) =====================
    let y = margin;

    if (logoRef.current) {
      const img = logoRef.current;
      const w = 104;
      const h = img.naturalWidth ? (img.naturalHeight / img.naturalWidth) * w : 32;
      docPdf.addImage(img, "PNG", centerX - w / 2, y, w, h);
      y += h + 6;
    }
    docPdf.setFont("helvetica", "italic").setFontSize(7.5).setTextColor(...MUTED);
    docPdf.text(POWERED_BY, centerX, y, { align: "center" });
    y += 18;
    hairline(y);
    y += 22;

    // Title
    eyebrow("Release of Information", margin, y);
    y += 16;
    docPdf.setFont("helvetica", "bold").setFontSize(15).setTextColor(...INK);
    docPdf.text("Authorization to Share Information with Researcher", margin, y);
    y += 24;

    // Patient details — clean two-column grid, no heavy panel
    const detailRows = [
      ["Name", patient.name],
      ["Date of Birth", patient.dob],
      ["MRN", patient.mrn],
      // Display labels ("IPF" prints as "ILD"); the stored value is untouched.
      [
        "Diagnosis",
        Array.isArray(patient.diagnosis)
          ? patient.diagnosis.map(diagnosisLabel)
          : diagnosisLabel(patient.diagnosis),
      ],
    ];
    const colW = contentWidth / 2;
    detailRows.forEach(([label, value], i) => {
      const col = i % 2;
      const rowTop = y + Math.floor(i / 2) * 30;
      const x = margin + col * colW;
      eyebrow(label, x, rowTop);
      docPdf.setFont("helvetica", "normal").setFontSize(10.5).setTextColor(...INK);
      docPdf.text(`${value || "—"}`, x, rowTop + 14, { maxWidth: colW - 16 });
    });
    y += Math.ceil(detailRows.length / 2) * 30 + 8;
    hairline(y);
    y += 18;

    // Consent body — comfortable leading
    docPdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(...SLATE);
    const wrapped = docPdf.splitTextToSize(consentText, contentWidth);
    wrapped.forEach((line) => {
      docPdf.text(line, margin, y);
      y += 12.6;
    });
    y += 16;

    // Signature — sits on a baseline rule (no boxed look)
    eyebrow("Signature", margin, y);
    y += 6;
    docPdf.addImage(getSignatureDataUrl(), "PNG", margin, y, 200, 50);
    y += 52;
    docPdf.setDrawColor(...HAIRLINE);
    docPdf.setLineWidth(0.8);
    docPdf.line(margin, y, margin + 230, y);
    y += 16;
    eyebrow("Printed Name", margin, y);
    eyebrow("Signed", margin + 260, y);
    docPdf.setFont("helvetica", "normal").setFontSize(10).setTextColor(...INK);
    docPdf.text(printName, margin, y + 14);
    docPdf.text(submittedAt.toLocaleString(), margin + 260, y + 14);

    // ===== PAGE 2: audit trail ==========================================
    docPdf.addPage();
    y = margin;

    if (logoRef.current) {
      const img = logoRef.current;
      const w = 84;
      const h = img.naturalWidth ? (img.naturalHeight / img.naturalWidth) * w : 26;
      docPdf.addImage(img, "PNG", centerX - w / 2, y, w, h);
      y += h + 14;
    }
    docPdf.setFont("helvetica", "bold").setFontSize(14).setTextColor(...INK);
    docPdf.text("eSignature Audit Trail", centerX, y, { align: "center" });
    y += 14;
    docPdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(...MUTED);
    docPdf.text(
      `Authorization to Share Information   •   MRN: ${patient.mrn || "—"}`,
      centerX,
      y,
      { align: "center" }
    );
    y += 16;
    hairline(y);
    y += 26;

    const events = [
      {
        label: "Authorization Link Created",
        desc: `Created for ${patient.name} (MRN: ${patient.mrn || "—"}) on ${fmtDate(patient.createdAt)}.`,
      },
      {
        label: "SMS Consent Link Sent",
        desc: patient.smsLinkSent
          ? `Sent to ${patient.phone} on ${fmtDate(patient.smsSentAt)}.`
          : "No SMS link recorded.",
      },
      {
        label: "Identity Verified",
        desc: `Date of birth confirmed by signer on ${fmtDate(dobVerifiedAtRef.current)}.`,
      },
      {
        label: "Patient Opened eSign Link",
        desc: `Opened on ${fmtDate(openedAtRef.current)}.`,
      },
      {
        label: "Consent Reviewed & Approved",
        desc: `Marked "I approve this document" on ${fmtDate(approvedAtRef.current)}.`,
      },
      {
        label: "Consent Signed & Submitted",
        desc: `Signed by ${printName} on ${fmtDate(submittedAt)}.`,
      },
      {
        label: "Signer Environment",
        desc: `IP Address: ${clientIP}\nDevice: ${userAgent}`,
      },
    ];

    const dotX = margin + 3;
    const textX = margin + 22;
    const textW = pageWidth - textX - margin;

    events.forEach((event, i) => {
      docPdf.setFont("helvetica", "normal").setFontSize(9);
      const descLines = docPdf.splitTextToSize(event.desc, textW);
      const blockHeight = 15 + descLines.length * 11 + 16;

      if (i < events.length - 1) {
        docPdf.setDrawColor(199, 210, 254); // indigo-200
        docPdf.setLineWidth(1.2);
        docPdf.line(dotX, y + 3, dotX, y + blockHeight);
      }
      docPdf.setFillColor(...INDIGO);
      docPdf.circle(dotX, y, 3.2, "F");

      docPdf.setFont("helvetica", "bold").setFontSize(10).setTextColor(...INK);
      docPdf.text(event.label, textX, y + 3);
      docPdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(...SLATE);
      docPdf.text(descLines, textX, y + 15);

      y += blockHeight;
    });

    // Footer
    docPdf.setDrawColor(...HAIRLINE);
    docPdf.line(margin, pageHeight - 56, pageWidth - margin, pageHeight - 56);
    docPdf.setFont("helvetica", "normal").setFontSize(8).setTextColor(...MUTED);
    docPdf.text("Generated by the RegX eSignature System", centerX, pageHeight - 40, {
      align: "center",
    });
    docPdf.text(POWERED_BY, centerX, pageHeight - 28, { align: "center" });

    return docPdf;
  };

  // ---- Step 3: submit ----------------------------------------------------
  const handleSubmit = async () => {
    if (!hasInk) {
      setSubmitError("Please sign in the box above before submitting.");
      return;
    }
    setSubmitError("");
    setSaving(true);
    try {
      const submittedAt = new Date();

      setSaveStage("Preparing your document…");
      const docPdf = buildPdf(submittedAt);
      const blob = docPdf.output("blob");

      setSaveStage("Uploading…");
      const fileName = `consent_${patient.mrn || "noMRN"}_${Date.now()}.pdf`;
      const storageRef = ref(storage, `consents/${fileName}`);
      await uploadBytes(storageRef, blob, { contentType: "application/pdf" });
      const url = await getDownloadURL(storageRef);

      setSaveStage("Saving…");
      // One call replaces the consent write AND the "mark this row done" update.
      // The patient's details are no longer sent from here — the function reads
      // them from the staged record it finds by token, so a consent can only
      // ever name the person the token belongs to.
      const resp = await fetch(`${FUNCTIONS_BASE}/esignSubmit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          siteId: site,
          token,
          printedName: printName,
          ipAddress: clientIP,
          deviceInfo: userAgent,
          consentUrl: url,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `Saving failed (${resp.status}).`);
      }

      setDone(true);
    } catch (err) {
      console.error("Failed to save signed consent:", err);
      setSubmitError("Something went wrong saving your form. Please try again.");
    } finally {
      setSaving(false);
      setSaveStage("");
    }
  };

  // ---- Render ------------------------------------------------------------
  if (loading) {
    return (
      <Shell>
        <Card>
          <div className="flex items-center justify-center gap-2 text-slate-400 py-10">
            <FiLoader className="animate-spin" /> Loading your form…
          </div>
        </Card>
      </Shell>
    );
  }

  if (!patient) {
    return (
      <Shell>
        <Card>
          <div className="text-center py-8">
            <FiAlertCircle className="mx-auto text-rose-300 mb-3" size={40} />
            <h2 className="text-lg font-bold text-white">This link isn't valid</h2>
            <p className="text-slate-400 mt-2 text-sm leading-relaxed">
              The link may be incorrect or has already been used. Please contact our office for a new link.
            </p>
          </div>
        </Card>
      </Shell>
    );
  }

  if (patient.expired) {
    return (
      <Shell>
        <Card>
          <div className="text-center py-8">
            <FiClock className="mx-auto text-amber-300 mb-3" size={40} />
            <h2 className="text-lg font-bold text-white">This link has expired</h2>
            <p className="text-slate-400 mt-2 text-sm leading-relaxed">
              This authorization link has already been used or is no longer active. Please contact our office if you need to sign again.
            </p>
          </div>
        </Card>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <Card>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30 flex items-center justify-center mb-4">
              <FiCheckCircle className="text-emerald-300" size={32} />
            </div>
            <h2 className="text-xl font-bold text-white">You're all set, thank you!</h2>
            <p className="text-slate-300 mt-3 leading-relaxed text-sm">
              Your authorization has been received. Our research team will review your records and reach out if a study looks like a good fit for you.
            </p>
            <p className="text-slate-500 text-xs mt-4">You can now close this page.</p>
          </motion.div>
        </Card>
      </Shell>
    );
  }

  const steps = ["Identity", "Review", "Sign"];
  const summary = [
    {
      Icon: FiFileText,
      title: "We'll look at your medical records",
      sub: "So our research team can review your health history.",
    },
    {
      Icon: FiSearch,
      title: "To see if a study could fit you",
      sub: "We're checking whether a research study might help.",
    },
    {
      Icon: FiInfo,
      title: "This is not signing up for anything",
      sub: "It's only permission to look — nothing more.",
    },
    {
      Icon: FiRefreshCw,
      title: "You can change your mind anytime",
      sub: "You may take back this permission whenever you want.",
    },
  ];

  return (
    <Shell>
      {/* Progress */}
      <div className="flex items-center justify-center gap-2 mb-5">
        {steps.map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const complete = step > n;
          return (
            <div key={label} className="flex items-center gap-2">
              <div
                className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors ${
                  complete
                    ? "bg-indigo-500 text-white"
                    : active
                    ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white ring-1 ring-white/20"
                    : "bg-white/10 text-slate-400"
                }`}
              >
                {complete ? <FiCheck size={14} /> : n}
              </div>
              {i < steps.length - 1 && (
                <div className={`h-px w-6 sm:w-10 ${step > n ? "bg-indigo-400/60" : "bg-white/15"}`} />
              )}
            </div>
          );
        })}
      </div>

      <Card>
        <AnimatePresence mode="wait">
          {/* STEP 1 — identity */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 14 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -14 }}
              transition={{ duration: 0.22 }}
            >
              <SectionHeader>Confirm it's you</SectionHeader>
              <h2 className="text-xl font-bold text-white">Enter your date of birth</h2>
              <p className="text-slate-400 text-sm mt-1.5 mb-5">
                This is just so we know the form is really for you.
              </p>
              <label className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1.5">
                Date of birth
              </label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">Month</p>
                  <select
                    value={dobMonth}
                    onChange={(e) => setDobMonth(e.target.value)}
                    className={SELECT_CLASS}
                  >
                    <option value="">MM</option>
                    {["01","02","03","04","05","06","07","08","09","10","11","12"].map((m, i) => (
                      <option key={m} value={m}>
                        {new Date(2000, i).toLocaleString("default", { month: "short" })}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">Day</p>
                  <select
                    value={dobDay}
                    onChange={(e) => setDobDay(e.target.value)}
                    className={SELECT_CLASS}
                  >
                    <option value="">DD</option>
                    {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">Year</p>
                  <select
                    value={dobYear}
                    onChange={(e) => setDobYear(e.target.value)}
                    className={SELECT_CLASS}
                  >
                    <option value="">YYYY</option>
                    {Array.from(
                      { length: new Date().getFullYear() - 1919 },
                      (_, i) => String(new Date().getFullYear() - i)
                    ).map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
              {dobError && (
                <p className="text-rose-300 text-sm mt-2 flex items-center gap-1.5">
                  <FiAlertCircle size={14} /> {dobError}
                </p>
              )}
              <div className="mt-6">
                <PrimaryButton onClick={handleVerifyDob}>
                  Continue <FiArrowRight size={18} />
                </PrimaryButton>
              </div>
            </motion.div>
          )}

          {/* STEP 2 — review & agree */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 14 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -14 }}
              transition={{ duration: 0.22 }}
            >
              <SectionHeader>Review &amp; agree</SectionHeader>
              <h2 className="text-xl font-bold text-white mb-4">What you're agreeing to</h2>

              <div className="space-y-2.5">
                {summary.map(({ Icon, title, sub }) => (
                  <div
                    key={title}
                    className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3"
                  >
                    <span className="shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500/25 to-blue-500/25 ring-1 ring-white/10 flex items-center justify-center">
                      <Icon className="text-purple-200" size={17} />
                    </span>
                    <div>
                      <p className="font-semibold text-slate-100 text-sm leading-snug">{title}</p>
                      <p className="text-xs text-slate-400 leading-snug mt-0.5">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-sm rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-0.5">
                <p className="text-slate-400">
                  <span className="text-slate-500 text-xs uppercase tracking-wider">Name</span>
                  <br />
                  <span className="text-slate-100">{patient.name}</span>
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowFullText((s) => !s)}
                className="mt-4 text-purple-300 hover:text-purple-200 font-medium text-sm underline underline-offset-2 transition"
              >
                {showFullText ? "Hide the full form" : "Read the full legal form"}
              </button>
              <AnimatePresence initial={false}>
                {showFullText && (
                  <motion.pre
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden whitespace-pre-wrap font-sans text-xs text-slate-400 mt-2 bg-black/20 border border-white/10 rounded-xl p-3 leading-relaxed"
                  >
                    {consentText}
                  </motion.pre>
                )}
              </AnimatePresence>

              <div className="mt-5">
                <label className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1.5">
                  Type your full name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Jane Smith"
                  value={printName}
                  onChange={(e) => setPrintName(e.target.value)}
                  className={INPUT_CLASS}
                />
              </div>

              <label className="flex items-start gap-3 mt-4 text-slate-200 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={approved}
                  onChange={(e) => handleApproveToggle(e.target.checked)}
                  className="mt-0.5 w-5 h-5 accent-indigo-500"
                />
                <span>I have read this and I agree to share my medical records with the research team.</span>
              </label>

              {reviewError && (
                <p className="text-rose-300 text-sm mt-3 flex items-center gap-1.5">
                  <FiAlertCircle size={14} /> {reviewError}
                </p>
              )}

              <div className="flex gap-3 mt-6">
                <GhostButton onClick={() => setStep(1)}>
                  <FiArrowLeft size={16} /> Back
                </GhostButton>
                <div className="flex-1">
                  <PrimaryButton onClick={handleContinueToSign}>
                    Continue to sign <FiArrowRight size={18} />
                  </PrimaryButton>
                </div>
              </div>
            </motion.div>
          )}

          {/* STEP 3 — sign */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 14 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -14 }}
              transition={{ duration: 0.22 }}
            >
              <SectionHeader>Signature</SectionHeader>
              <h2 className="text-xl font-bold text-white">Sign to finish</h2>
              <p className="text-slate-400 text-sm mt-1.5 mb-4">
                Use your finger or a stylus to sign inside the box below.
              </p>

              <div ref={containerRef} className="rounded-2xl p-1.5 bg-white/[0.06] border border-white/10">
                <canvas
                  ref={canvasRef}
                  className="w-full h-[190px] touch-none rounded-xl bg-white"
                  onMouseDown={handleStart}
                  onMouseMove={handleMove}
                  onMouseUp={handleEnd}
                  onMouseLeave={handleEnd}
                  onTouchStart={handleStart}
                  onTouchMove={handleMove}
                  onTouchEnd={handleEnd}
                />
              </div>

              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-slate-400">
                  {hasInk ? "Looks good!" : "Sign above using your finger or stylus."}
                </span>
                <button
                  onClick={handleClear}
                  type="button"
                  className="text-sm text-slate-300 hover:text-white underline underline-offset-2 transition"
                >
                  Clear
                </button>
              </div>

              {submitError && (
                <p className="text-rose-300 text-sm mt-3 flex items-center gap-1.5">
                  <FiAlertCircle size={14} /> {submitError}
                </p>
              )}

              <div className="flex gap-3 mt-6">
                <GhostButton onClick={() => setStep(2)} disabled={saving}>
                  <FiArrowLeft size={16} /> Back
                </GhostButton>
                <div className="flex-1">
                  <PrimaryButton onClick={handleSubmit} disabled={saving}>
                    {saving ? (
                      <>
                        <FiLoader className="animate-spin" size={18} />
                        {saveStage || "Submitting…"}
                      </>
                    ) : (
                      <>
                        Submit &amp; finish <FiCheck size={18} />
                      </>
                    )}
                  </PrimaryButton>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>
    </Shell>
  );
}
